import { defineStore } from 'pinia';
import { auth, db } from '@/firebase';
import { Expense } from '@/types/expense';
import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  setDoc,
  deleteDoc,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useAuthStore } from '@/store/authstore';
import { useFilesStore } from '@/store/filestore';
import { useInvoiceStore } from '@/store/invoicestore';
import { toMonthKey } from '@/utils/format';
import { FREE_TIER_LIMIT } from '@/utils/freeTier';

function stripUndefined<T extends object>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as T;
}

export const useExpenseStore = defineStore('expenseStore', {
  state: () => ({
    expenses: [] as Expense[],
    expensesByMonth: {} as Record<string, Expense[]>,
    currentMonths: [] as string[],
    allExpensesFetched: false,
    expenseCount: null as number | null,
    recentExpenses: [] as Expense[],
    error: null as string | null,
    loading: false,
  }),

  actions: {
    // Patches the byMonth cache in place with a document we just wrote to Firestore,
    // instead of invalidating the whole cache and forcing a full collection re-read.
    _patchExpenseCache(expense: Expense) {
      for (const key of Object.keys(this.expensesByMonth)) {
        this.expensesByMonth[key] = this.expensesByMonth[key].filter((e) => e.id !== expense.id);
      }

      const key = toMonthKey(expense.date);
      if (key && (this.expensesByMonth[key] || this.allExpensesFetched)) {
        this.expensesByMonth[key] = [...(this.expensesByMonth[key] || []), expense];
      }

      this.expenses = this.currentMonths
        .flatMap((m) => this.expensesByMonth[m] || [])
        .sort((a, b) => (a.date < b.date ? 1 : -1));
    },

    _removeExpenseFromCache(id: string) {
      for (const key of Object.keys(this.expensesByMonth)) {
        this.expensesByMonth[key] = this.expensesByMonth[key].filter((e) => e.id !== id);
      }
      this.expenses = this.currentMonths
        .flatMap((m) => this.expensesByMonth[m] || [])
        .sort((a, b) => (a.date < b.date ? 1 : -1));
    },

    async fetchExpenseCount() {
      if (this.expenseCount !== null) return;
      try {
        const user = auth.currentUser;
        if (!user) return;
        const expensesRef = collection(db, `users/${user.uid}/expenses`);
        const snap = await getCountFromServer(expensesRef);
        this.expenseCount = snap.data().count;
      } catch (err) {
        console.error(err);
      }
    },

    async addExpense(expense: Omit<Expense, 'id'>): Promise<string | null> {
      this.error = null;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Bruger er ikke logget ind');

        const authStore = useAuthStore();
        if (!authStore.user?.hasAccess) {
          const invoiceStore = useInvoiceStore();
          await Promise.all([this.fetchExpenseCount(), invoiceStore.fetchInvoiceCount()]);
          const total = (this.expenseCount ?? 0) + (invoiceStore.invoiceCount ?? 0);
          if (total >= FREE_TIER_LIMIT) {
            throw new Error(`Du har nået den gratis grænse på ${FREE_TIER_LIMIT} fakturaer og køb. Opgrader for at oprette flere.`);
          }
        }

        const expensesRef = collection(db, `users/${user.uid}/expenses`);
        const newDocRef = doc(expensesRef);
        await setDoc(newDocRef, stripUndefined(expense));

        const added: Expense = { id: newDocRef.id, ...expense };
        this._patchExpenseCache(added);
        if (this.expenseCount !== null) this.expenseCount += 1;
        return newDocRef.id;
      } catch (err: any) {
        this.error = err.message || 'Kunne ikke tilføje udgift';
        console.error(err);
        return null;
      }
    },

    async updateExpense(expense: Expense): Promise<void> {
      this.error = null;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Bruger er ikke logget ind');
        if (!expense.id) throw new Error('Udgifts-ID mangler');

        const { id, ...data } = expense;
        const docRef = doc(db, `users/${user.uid}/expenses`, id);
        await setDoc(docRef, stripUndefined(data));

        this._patchExpenseCache({ ...expense });
      } catch (err: any) {
        this.error = err.message || 'Kunne ikke opdatere udgift';
        console.error(err);
      }
    },

    async fetchExpenses(months: string[] = []): Promise<void> {
      this.error = null;
      this.loading = true;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Bruger er ikke logget ind');

        const expensesRef = collection(db, `users/${user.uid}/expenses`);

        if (months.length === 0) {
          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, '0');
          months = [`${y}-${m}`];
        }

        for (const monthStr of months) {
          if (this.expensesByMonth[monthStr] || this.allExpensesFetched) continue;

          const [year, month] = monthStr.split('-');
          const start = `${year}-${month}-01`;
          const endDate = new Date(parseInt(year), parseInt(month), 0);
          const end = `${year}-${month}-${String(endDate.getDate()).padStart(2, '0')}`;

          const q = query(
            expensesRef,
            where('date', '>=', start),
            where('date', '<=', end)
          );
          const snap = await getDocs(q);
          this.expensesByMonth[monthStr] = snap.docs.map(d => {
            const { id: _stored, ...data } = d.data();
            return { id: d.id, ...(data as Omit<Expense, 'id'>) };
          });
        }

        this.currentMonths = months;
        this.expenses = months
          .flatMap(m => this.expensesByMonth[m] || [])
          .sort((a, b) => (a.date < b.date ? 1 : -1));
      } catch (err: any) {
        this.error = 'Kunne ikke hente udgifter';
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async fetchAllExpenses(): Promise<void> {
      if (this.allExpensesFetched) return;

      this.error = null;
      this.loading = true;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Bruger er ikke logget ind');

        const expensesRef = collection(db, `users/${user.uid}/expenses`);
        const snap = await getDocs(expensesRef);
        this.expenses = snap.docs
          .map(d => {
            const { id: _stored, ...data } = d.data();
            return { id: d.id, ...(data as Omit<Expense, 'id'>) };
          })
          .sort((a, b) => (a.date < b.date ? 1 : -1));

        const byMonth: Record<string, Expense[]> = {};
        for (const expense of this.expenses) {
          const key = toMonthKey(expense.date);
          if (!key) continue;
          (byMonth[key] ??= []).push(expense);
        }
        this.expensesByMonth = byMonth;
        this.allExpensesFetched = true;
        this.currentMonths = Object.keys(byMonth);
      } catch (err: any) {
        this.error = 'Kunne ikke hente udgifter';
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async fetchRecentExpenses(count = 10): Promise<void> {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Bruger er ikke logget ind');

        const expensesRef = collection(db, `users/${user.uid}/expenses`);
        const q = query(expensesRef, orderBy('date', 'desc'), limit(count));
        const snap = await getDocs(q);
        this.recentExpenses = snap.docs.map(d => {
          const { id: _stored, ...data } = d.data();
          return { id: d.id, ...(data as Omit<Expense, 'id'>) };
        });
      } catch (err: any) {
        console.error(err);
      }
    },

    async deleteExpense(expense: Expense): Promise<void> {
      this.error = null;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Bruger er ikke logget ind');
        if (!expense.id) throw new Error('Udgifts-ID mangler');

        const docRef = doc(db, `users/${user.uid}/expenses`, expense.id);
        await deleteDoc(docRef);

        if (expense.receiptUrl) {
          const filesStore = useFilesStore();
          await filesStore.deleteExpenseReceipt(expense.id).catch(() => {});
        }

        this._removeExpenseFromCache(expense.id);
        if (this.expenseCount !== null) this.expenseCount = Math.max(0, this.expenseCount - 1);
      } catch (err: any) {
        this.error = err.message || 'Kunne ikke slette udgift';
        console.error(err);
      }
    },
  },
});
